import { Component, EventEmitter, Input, Output } from '@angular/core';
import {
  DmrResponse,
  KsfService,
  LibUtilityService,
  applyForKsfInsurancePayload,
  KsfAcceptConditions,
  ksfCustomerInfo,
  UpdateKsfTemplate,
  ResetPriceCalculator,
} from 'projects/shared/src/public-api';

@Component({
    selector: 'lib-ksf-accept-conditions',
    templateUrl: './ksf-accept-conditions.component.html',
    styleUrls: ['./ksf-accept-conditions.component.scss'],
    standalone: false
})
export class KsfAcceptConditionsComponent {
  // Event emitted to the parent component
  @Output() nextStepEvent = new EventEmitter();

  // Input properties to receive data from the parent component
  @Input() chargingStand: boolean = false;
  @Input() textDirectedToCustomer: boolean = false;
  @Input() ksfCustomerInfo: ksfCustomerInfo = {} as ksfCustomerInfo;
  @Input() ksfTemplate: UpdateKsfTemplate = {} as UpdateKsfTemplate;
  @Input() vehicleFromDmr: DmrResponse = {} as DmrResponse;
  @Input() carOwnershipType: string = '';
  @Input() selectedPrice: { name: string; value: number } = {
    name: '',
    value: 0,
  };
  @Input() ksfAcceptConditions: KsfAcceptConditions = {
    acceptedTradingConditions: false,
    acceptedInfoIsCorrect: false,
    acceptedConsent: false,
    acceptedAsgMarketing: false,
    acceptedChargingStandConsultant: false,
    acceptedChargingStandCustomer: false,
  };
  @Input() dealerId: number | null = null;
  @Input() leadOrigin: number = 2;

  // Internal state variables
  errorInChargingStandCustomer: boolean = false;
  errorInChargingStandConsultant: boolean = false;
  errorInTradingConditionsCheckbox: boolean = false;
  errorInInfoIsCorrecCheckbox: boolean = false;
  errorInConsentCheckbox: boolean = false;

  applyForInsuranceLoading: boolean = false;

  constructor(
    private ksfService: KsfService,
    public resetPriceCalculator: ResetPriceCalculator,
    private libUtilityService: LibUtilityService,
  ) {
    // Subscribe to reset events
    this.resetPriceCalculator.resetPriceCalculatorValues.subscribe(() => {
      this.resetKsfInfo();
    });

    this.resetPriceCalculator.resetKsfValues.subscribe(() => {
      this.resetKsfInfo();
    });
  }

  /**
   * Reset the info and clear error flags
   * @returns void
   */
  resetKsfInfo(): void {
    this.ksfAcceptConditions = {
      acceptedTradingConditions: false,
      acceptedInfoIsCorrect: false,
      acceptedConsent: false,
      acceptedAsgMarketing: false,
      acceptedChargingStandConsultant: false,
      acceptedChargingStandCustomer: false,
    };
    this.errorInChargingStandCustomer = false;
    this.errorInChargingStandConsultant = false;
    this.errorInTradingConditionsCheckbox = false;
    this.errorInInfoIsCorrecCheckbox = false;
    this.errorInConsentCheckbox = false;
    this.applyForInsuranceLoading = false;
  }

  /**
   * Submits an application for KSF insurance after validating required conditions.
   * @returns void
   */
  applyForKSF(): void {
    // Reset error flags before validation
    this.errorInTradingConditionsCheckbox = false;
    this.errorInInfoIsCorrecCheckbox = false;
    this.errorInChargingStandCustomer = false;
    this.errorInChargingStandConsultant = false;

    // Validate required conditions and set error flags if necessary
    const isValid =
      this.ksfAcceptConditions.acceptedTradingConditions &&
      this.ksfAcceptConditions.acceptedInfoIsCorrect &&
      (!this.chargingStand ||
        (this.ksfAcceptConditions.acceptedChargingStandCustomer &&
          this.ksfAcceptConditions.acceptedChargingStandConsultant));

    if (!isValid) {
      this.errorInTradingConditionsCheckbox =
        !this.ksfAcceptConditions.acceptedTradingConditions;
      this.errorInInfoIsCorrecCheckbox =
        !this.ksfAcceptConditions.acceptedInfoIsCorrect;
      this.errorInChargingStandCustomer =
        this.chargingStand &&
        !this.ksfAcceptConditions.acceptedChargingStandCustomer;
      this.errorInChargingStandConsultant =
        this.chargingStand &&
        !this.ksfAcceptConditions.acceptedChargingStandConsultant;
      // Exit function if validation fails
      return;
    }

    // Start loading indicator
    this.applyForInsuranceLoading = true;

    // Prepare the payload for the API request
    const applyForInsurancePayload: applyForKsfInsurancePayload = {
      templateId: this.ksfTemplate.templateId,
      dealerId: this.dealerId,
      leadOrigin: this.leadOrigin,
      addressStreet: this.ksfCustomerInfo.selectedAddressInfo.adresse.vejnavn,
      addressBuildingNumber:
        this.ksfCustomerInfo.selectedAddressInfo.adresse.husnr,
      addressDoor: this.ksfCustomerInfo.selectedAddressInfo.adresse.dør,
      addressFloor: this.ksfCustomerInfo.selectedAddressInfo.adresse.etage,
      addressAdditionalCity:
        this.ksfCustomerInfo.selectedAddressInfo.adresse.supplerendebynavn,
      addressPostalCode:
        this.ksfCustomerInfo.selectedAddressInfo.adresse.postnr,
      addressCity: this.ksfCustomerInfo.selectedAddressInfo.adresse.postnrnavn,
      addressStreetCode:
        +this.ksfCustomerInfo.selectedAddressInfo.adresse.vejkode,
      customerIdNumber: this.ksfCustomerInfo.cprNumber,
      customerIdType: 'CPR',
      customerFirstName: this.ksfCustomerInfo.firstname,
      customerLastName: this.ksfCustomerInfo.lastname,
      customerCompanyName: null,
      customerEmail: this.ksfCustomerInfo.email,
      customerPhone: `${this.ksfCustomerInfo.phoneNumber}`,
      vehicleRegistrationNumber: this.vehicleFromDmr.registrationPlate,
      vehicleEngineVolume: this.vehicleFromDmr.engineVolume,
      vehicleVin: this.vehicleFromDmr.vin,
      vehicleMake: this.vehicleFromDmr.make,
      vehicleModel: this.vehicleFromDmr.model,
      vehicleVariant: this.vehicleFromDmr.variant,
      vehicleHorsepower: this.vehicleFromDmr.horsepower,
      vehiclePrice: this.selectedPrice.value,
      vehicleFuelType: this.vehicleFromDmr.fuelType,
      vehiclePossession: this.carOwnershipType,
      vehicleCode: this.vehicleFromDmr.code,
      vehicleYear: this.vehicleFromDmr.year,
      vehicleOwnWeight: this.vehicleFromDmr.ownWeight,
      vehicleTotalWeight: this.vehicleFromDmr.totalWeight,
      vehicleBodyType: this.vehicleFromDmr.bodyType,
      vehicleFirstRegistrationDate: this.vehicleFromDmr.firstRegistrationDate,
      vehicleMileage: this.vehicleFromDmr.mileage * 1000,
      insuranceAddonKfDriverCoverage: false,
      insuranceAddonKfRoadsideAssist: false,
      insuranceAddonKfExtendedCarCoverage: false,
      insuranceAddonKfParkingDamage: false,
      chargingStand: this.chargingStand,
      consentChargingStandCustomer:
        this.ksfAcceptConditions.acceptedChargingStandCustomer,
      consentChargingStandConsultant:
        this.ksfAcceptConditions.acceptedChargingStandConsultant,
      consentValidData: this.ksfAcceptConditions.acceptedInfoIsCorrect,
      consentAsgMarketing: this.ksfAcceptConditions.acceptedAsgMarketing,
      vehicleExistingInsuranceCompany:
        this.vehicleFromDmr.currentInsuranceCompany,
    };

    // Submit the application for KSF insurance
    this.ksfService.applyForKsf(applyForInsurancePayload).subscribe({
      next: () => {
        this.applyForInsuranceLoading = false;
        this.nextStepEvent.emit();
      },
      error: () => {
        this.applyForInsuranceLoading = false;
        this.libUtilityService.notification(
          'error',
          'Fejl',
          'Der er sket en fejl - Kontakt os på support@asgcare.dk',
        );
      },
    });
  }
}
